import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContactlessNewLink, ContactlessSite } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { Alert, Button, Card, CheckboxField, ConfirmButton, SelectField, Spinner, TextField } from '../../components/ui';
import { useScopedSettings } from '../sites/api';
import { openPdf } from './api';

interface Draft {
  enabled?: boolean;
  qrMode?: 'static' | 'rotating';
  maxSessionsPerHour?: number;
  requireNearSite?: boolean;
}

/**
 * Site card "Contactless" (contactless.md section 4): on or off, static or rotating code, print the poster, make a new link,
 * the near-site check and the sessions-per-hour limit. W23 mounts it on the site page. Settings save through the site settings
 * route (needs sites.manage); the link, new link and poster need contactless.manage.
 */
export function ContactlessCard({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const canSetUp = useCan('contactless.manage');
  const canEdit = useCan('sites.manage');
  const settings = useScopedSettings(`/sites/${siteId}/settings`, 'contactless');
  const [draft, setDraft] = useState<Draft>({});
  const siteKey = ['contactless', 'site', siteId];
  const site = useQuery({ queryKey: siteKey, enabled: canSetUp, queryFn: () => api.get(`/contactless/sites/${siteId}`, { schema: ContactlessSite }) });
  const newLink = useMutation({
    mutationFn: () => api.post(`/contactless/sites/${siteId}/new-link`, { schema: ContactlessNewLink }),
    onSuccess: () => qc.invalidateQueries({ queryKey: siteKey }),
  });
  const poster = useMutation({
    mutationFn: () => api.download(`/contactless/sites/${siteId}/poster.pdf`),
    onSuccess: ({ blob, filename }) => openPdf(blob, filename ?? 'contactless-poster.pdf'),
  });

  if (settings.query.isPending) return <Spinner />;
  if (settings.query.isError) return <ErrorPanel error={settings.query.error} onRetry={() => void settings.query.refetch()} />;
  const saved = settings.values ?? {};
  const value = <K extends keyof Draft>(k: K): NonNullable<Draft[K]> => (draft[k] ?? saved[k]) as NonNullable<Draft[K]>;
  const changed = Object.keys(draft).length > 0;
  // The saved mode decides whether a poster can be printed; an unsaved change does not.
  const rotating = (site.data?.qrMode ?? saved['qrMode']) === 'rotating';
  const save = () =>
    settings.save.mutate(draft as Record<string, unknown>, {
      onSuccess: () => {
        setDraft({});
        void qc.invalidateQueries({ queryKey: siteKey });
      },
    });
  const copy = () => {
    const url = site.data?.url;
    if (url) void navigator.clipboard?.writeText(url).catch(() => undefined);
  };

  return (
    <Card
      title={t('siteF.contactless.title')}
      description={t('siteF.contactless.description')}
      footer={
        canEdit ? (
          <>
            <Button disabled={!changed} onClick={() => setDraft({})}>
              {t('settings.discard')}
            </Button>
            <Button variant="primary" disabled={!changed || settings.save.isPending} onClick={save}>
              {t('common.save')}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <ErrorLine error={settings.save.error} />
        <CheckboxField
          label={t('siteF.contactless.enabled')}
          hint={t('siteF.contactless.enabledHint')}
          disabled={!canEdit}
          checked={value('enabled')}
          onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
        />
        <SelectField label={t('siteF.contactless.qrMode')} disabled={!canEdit} value={value('qrMode')} onChange={(e) => setDraft({ ...draft, qrMode: e.target.value as 'static' | 'rotating' })}>
          <option value="static">{t('siteF.contactless.mode.static')}</option>
          <option value="rotating">{t('siteF.contactless.mode.rotating')}</option>
        </SelectField>
        <TextField
          label={t('siteF.contactless.perHour')}
          hint={t('siteF.contactless.perHourHint')}
          type="number"
          min={0}
          disabled={!canEdit}
          value={String(value('maxSessionsPerHour'))}
          onChange={(e) => setDraft({ ...draft, maxSessionsPerHour: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
        />
        <CheckboxField
          label={t('siteF.contactless.nearSite')}
          hint={t('siteF.contactless.nearSiteHint')}
          disabled={!canEdit}
          checked={value('requireNearSite')}
          onChange={(e) => setDraft({ ...draft, requireNearSite: e.target.checked })}
        />

        {canSetUp ? (
          <div className="space-y-3 border-t border-slate-200 pt-4">
            {site.isError ? <ErrorLine error={site.error} /> : null}
            {rotating ? <Alert kind="info">{t('siteF.contactless.rotatingNote')}</Alert> : null}
            {site.data?.url ? (
              <div>
                <p className="text-sm font-medium text-slate-800">{t('siteF.contactless.link')}</p>
                <p className="mt-1 break-all rounded-md bg-slate-50 p-2 font-mono text-xs text-slate-800">{site.data.url}</p>
                <p className="mt-1 text-xs text-slate-600">{t('siteF.contactless.version', { n: site.data.linkVersion })}</p>
              </div>
            ) : null}
            <ErrorLine error={newLink.error} />
            <ErrorLine error={poster.error} />
            {newLink.isSuccess ? <Alert kind="success">{t('siteF.contactless.newLinkDone')}</Alert> : null}
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => poster.mutate()} disabled={rotating || poster.isPending}>
                {t('siteF.contactless.poster')}
              </Button>
              <Button onClick={copy} disabled={!site.data?.url}>
                {t('siteF.contactless.copy')}
              </Button>
              <ConfirmButton label={t('siteF.contactless.newLink')} confirmLabel={t('siteF.contactless.newLinkConfirm')} pending={newLink.isPending} onConfirm={() => newLink.mutate()} />
            </div>
            <p className="text-xs text-slate-600">{t('siteF.contactless.newLinkWarning')}</p>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
